import {computed, inject, Injectable, NgZone, Signal, signal, WritableSignal} from '@angular/core';
import {AppStore, initialAppState} from '../../stores/app.store';
import {Meta} from '@angular/platform-browser';
import {environment} from '../../../environments/environment';
import {ListViews} from '../../models/list/list-views.model';
import {LoginType} from '../../types/login/login.type';
import {STATIC_CONFIGS} from 'src/configs/static.config';
import {IMAGE_PATH} from 'src/app/constants/image-paths.constants';
import {PluginResultData} from '@capacitor/core/types/definitions';
import {HandleExternalEventCallback} from '../../types/handle-external-event-callback.type';
import {ThemeService} from '../theme/theme.service';
import {HistoryStoreItem} from '../../models/history/history-store-item.model';
import {ListItemTypeEnum} from '../../enums/list-item/list-item-type.enum';
import {PanelLocationEnum} from '../../enums/panel-location.enum';

@Injectable({ providedIn: 'root' })
export class AppService {
    protected showSplashScreen: WritableSignal<boolean>;
    private hasUnsavedTagChanges: boolean = false;
    private splashScreenActivatedTimestampInMs: number;
    private splashScreenTimeOut: number | undefined;
    private readonly appStore = inject(AppStore);
    private readonly ngZone = inject(NgZone);
    private readonly meta = inject(Meta);
    private readonly themeService = inject(ThemeService);

    public constructor() {
        this.meta.updateTag({
            name: 'apt-version',
            content: environment.version
        });
        this.showSplashScreen = signal(false);
        this.splashScreenActivatedTimestampInMs = 0;
    }

    public showSearchMenu(): void {
        this.appStore.update({ showSearchMenu: true });
    }

    public hideSearchMenu(): void {
        this.appStore.update({ showSearchMenu: false });
    }

    public showHistoryOverlay(): void {
        this.appStore.update({ isShowingHistoryOverlay: true });
    }

    public hideHistoryOverlay(): void {
        this.appStore.update({ isShowingHistoryOverlay: false });
    }

    public showDebugMenu(): void {
        this.appStore.update({ showDebugMenu: true });
    }

    public hideDebugMenu(): void {
        this.appStore.update({ showDebugMenu: false });
    }

    public setNavigationHistory(navigationHistory: Array<HistoryStoreItem>): void {
        this.appStore.update({ navigationHistory });
    }

    public setSearchTaskGlobalScope(state: boolean): void {
        this.setShowingSmallMenu(PanelLocationEnum.Sidebar, false);
        this.appStore.update({ isSearchAndTaskGlobalScope: state });
    }

    public handleExternalEvent<Type extends PluginResultData>(callback: HandleExternalEventCallback<Type>): HandleExternalEventCallback<Type> {
        let timeout: ReturnType<typeof setTimeout> | null;
        return (data, error) => {
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => {
                timeout = null;
                this.ngZone.run(() => callback.bind(this)(data, error));
            }, 200);
        };
    }

    public setup(): void {
        this.appStore.update({
            connectionErrorTimeStamp: initialAppState.connectionErrorTimeStamp,
            disconnected: initialAppState.disconnected,
        });
    }


    public setListView(listName: string, listViewType: ListItemTypeEnum): void {
        const newListViews: ListViews = { ...this.appStore.getValue().listViews };
        newListViews[listName] = listViewType;
        this.appStore.update({ listViews: newListViews });
    }

    public clearListViews(): void {
        this.appStore.update({ listViews: {} });
    }

    public setShowingSmallMenu(location: PanelLocationEnum, showMenu: boolean): void {
        if (location === PanelLocationEnum.Sidebar) {
            this.appStore.update({ isShowingShortMenuSidebar: showMenu });
        } else {
            this.appStore.update({ isShowingShortMenuContent: showMenu });
        }
    }

    public setIsNotShowingSearchOnGoingBack(isNotShowingSearchOnGoingBack: boolean): void {
        this.appStore.update({ isNotShowingSearchOnGoingBack });
    }

    public resetOnLogout(): void {
        this.appStore.update({
            showSearchMenu: initialAppState.showSearchMenu,
            isShowingHistoryOverlay: initialAppState.isShowingHistoryOverlay,
            isShowingShortMenuContent: initialAppState.isShowingShortMenuContent,
            isShowingShortMenuSidebar: initialAppState.isShowingShortMenuSidebar,
            isNotShowingSearchOnGoingBack: initialAppState.isNotShowingSearchOnGoingBack,
            navigationHistory: initialAppState.navigationHistory,
            toastDelayMS: initialAppState.toastDelayMS,
            connectionErrorTimeStamp: initialAppState.connectionErrorTimeStamp,
        });
    }

    public setCurrentVersion(currentVersion: string): void {
        this.appStore.update({ currentVersion });
    }

    public setToastDelayInMS(toastDelayMS: number): void {
        this.appStore.update({ toastDelayMS });
    }

    public setMaxImagesPerWorker(maxImagesPerWorker: number): void {
        this.appStore.update({ maxImagesPerWorker });
    }

    public setHasActionButton(hasActionButton: boolean): void {
        this.appStore.update({ hasActionButton });
    }

    public setInitialized(isInitialized: boolean): void {
        this.appStore.update({ isInitialized });
    }

    public toggleDeleteVaultWithoutPrompt(): void {
        const deleteVaultWithoutPrompt = !this.appStore.getValue().deleteVaultWithoutPrompt;
        this.appStore.update({ deleteVaultWithoutPrompt });
    }

    public setPartnerId(partnerId: string | null): void {
        this.appStore.update({ partnerId });
    }

    public setConnectionErrorTimeStamp(connectionErrorTimeStamp: number): void {
        this.appStore.update({ connectionErrorTimeStamp });
    }

    public setIsDisconnected(disconnected: boolean): void {
        this.appStore.update({ disconnected });
    }

    public setLastLoginType(loginType: LoginType): void {
        this.appStore.update({ lastLoginType: loginType });
    }

    public getBackgroundImageUrlSignal(): Signal<string> {
        return computed(() => {
            const monthInt: number = (new Date()).getMonth();
            const isDarkMode: Signal<boolean> = this.themeService.getIsDarkModeSignal();
            const monthlyImagesArray: Array<string> = isDarkMode() ? STATIC_CONFIGS.ui.backgroundsByMonth.dark : STATIC_CONFIGS.ui.backgroundsByMonth.light;
            return `${IMAGE_PATH}/${monthlyImagesArray[monthInt]}`;
        });
    }

    public setShowSplashScreen(showSplashScreen: boolean, minVisibleTimeInMs: number = 1000): void {
        const currentTimestamp: number = Date.now();
        const clearWindowTimeout = (): void => {
            if (this.splashScreenTimeOut !== undefined) {
                window.clearTimeout(this.splashScreenTimeOut);
                this.splashScreenTimeOut = undefined;
            }
        };

        if (showSplashScreen) {
            this.showSplashScreen.set(true);
            this.splashScreenActivatedTimestampInMs = currentTimestamp;
            clearWindowTimeout();
        } else {
            const minVisibleTimeExpired = this.splashScreenActivatedTimestampInMs + minVisibleTimeInMs <= currentTimestamp;

            if (minVisibleTimeExpired) {
                this.showSplashScreen.set(false);
            } else {
                const remainingMinVisibleTimeInMs = minVisibleTimeInMs - (currentTimestamp - this.splashScreenActivatedTimestampInMs);
                this.splashScreenTimeOut = window.setTimeout(() => {
                    this.showSplashScreen.set(false);
                    clearWindowTimeout();
                }, remainingMinVisibleTimeInMs);
            }
            this.splashScreenActivatedTimestampInMs = 0;
        }
    }

    public getShowSplashScreenSignal(): WritableSignal<boolean> {
        return this.showSplashScreen;
    }

    public setHasUnsavedTagChanges(hasUnsavedTagChanges: boolean): void {
        this.hasUnsavedTagChanges = hasUnsavedTagChanges;
    }

    public getHasUnsavedTagChanges(): boolean {
        return this.hasUnsavedTagChanges;
    }
}
